import {
  type QueryAuthorityInput,
  resolveQueryAuthorityState,
} from './memberCoachBookingState';

import type { Session, SessionDashboard } from '@/types';

// Once a Member holds an active Moments ("mento-cal") biweekly series, the
// coaching-sessions page narrows to that coaching relationship: every other
// matched coach and every session belonging to one of them is withheld. Before
// any biweekly exists — the chemistry-call stage — all matched coaches stay
// visible, which is what lets a Member compare two coaches they have chemistry
// calls with.
//
// Narrowing is deliberately keyed on `activeBiweekly[coachId].bookingSource ===
// 'moments'` rather than on any Cal-sourced recurrence. Cal never reports an
// authoritative series for a Member here (`Sessions::CoachCadence` synthesises a
// biweekly default for any anchor without a `recurringEvent`, including one-off
// chemistry calls), so treating a Cal cadence as an active series would narrow
// the page on a fabricated recurrence.

type ActiveBiweekly = NonNullable<SessionDashboard['activeBiweekly']>;

const MOMENTS_BOOKING_SOURCE = 'moments';

export interface BiweeklyCoachVisibility {
  // False whenever the page must render exactly as it does today. Callers can
  // skip their filtering work entirely when this is false.
  isNarrowed: boolean;
  isVisibleCoachId: (coachId: string) => boolean;
  isVisibleSession: (session: Session) => boolean;
}

export interface BiweeklyCoachVisibilityInput {
  activeBiweekly: ActiveBiweekly | undefined;
  // Rails `User.id` of every coach the Member is matched with.
  coachIds: readonly string[];
  // The dashboard query's own state, not a precomputed boolean: narrowing may
  // only run against a settled, non-error dashboard, and RTK Query retains the
  // last successful `data` across a rejected refetch. A caller that checked
  // presence and `isFetching` alone would keep narrowing on stale data after a
  // failed refresh — and every caller would have to re-derive that rule
  // correctly. `resolveQueryAuthorityState` owns it here instead.
  dashboardQuery: QueryAuthorityInput;
  // Timeline rows, used only to resolve Moments person ids (see
  // `isVisibleSession`). Safe to omit where no native rows are rendered.
  sessions?: readonly Session[];
}

// Rails `User.id`s of the coaches the Member holds an active Moments biweekly
// with, restricted to coaches the Member is actually matched with.
const visibleCoachIdsFrom = (
  activeBiweekly: ActiveBiweekly | undefined,
  coachIds: readonly string[],
): Set<string> => {
  const matched = new Set(coachIds);
  return new Set(
    Object.entries(activeBiweekly ?? {})
      .filter(
        ([coachId, series]) =>
          series.bookingSource === MOMENTS_BOOKING_SOURCE &&
          matched.has(coachId),
      )
      .map(([coachId]) => coachId),
  );
};

const activeSeriesIdsFrom = (
  activeBiweekly: ActiveBiweekly | undefined,
  visibleCoachIds: ReadonlySet<string>,
): Set<string> =>
  new Set(
    Object.entries(activeBiweekly ?? {})
      .filter(([coachId]) => visibleCoachIds.has(coachId))
      .map(([, series]) => series.seriesId)
      .filter((seriesId): seriesId is string => !!seriesId),
  );

const sessionSeriesId = (session: Session): string | null =>
  session.sourceAppointmentSeriesId || session.recurringId || null;

// A Member-viewer native row carries Moments person ids in `coachId`, never a
// Rails `User.id` (see `Session['sourceAppointmentIdentityScheme']`), so it
// cannot be compared against the dashboard's Rails-keyed `activeBiweekly`. The
// bridge is the series id: any native row on a visible coach's active series
// reveals that coach's person id, which then attributes their other native rows.
const visibleCoachPersonIdsFrom = (
  sessions: readonly Session[],
  activeSeriesIds: ReadonlySet<string>,
): Set<string> => {
  const personIds = new Set<string>();
  sessions.forEach((session) => {
    if (session.sourceAppointmentIdentityScheme !== 'moments-person') return;
    const seriesId = sessionSeriesId(session);
    if (seriesId && activeSeriesIds.has(seriesId))
      personIds.add(session.coachId);
  });
  return personIds;
};

export const resolveBiweeklyCoachVisibility = ({
  activeBiweekly,
  coachIds,
  dashboardQuery,
  sessions = [],
}: BiweeklyCoachVisibilityInput): BiweeklyCoachVisibility => {
  const visibleCoachIds = visibleCoachIdsFrom(activeBiweekly, coachIds);
  const isNarrowed =
    resolveQueryAuthorityState(dashboardQuery) === 'ready' &&
    visibleCoachIds.size > 0;
  const activeSeriesIds = activeSeriesIdsFrom(activeBiweekly, visibleCoachIds);
  const visibleCoachPersonIds = visibleCoachPersonIdsFrom(
    sessions,
    activeSeriesIds,
  );

  return {
    isNarrowed,
    isVisibleCoachId: (coachId) => !isNarrowed || visibleCoachIds.has(coachId),
    isVisibleSession: (session) => {
      if (!isNarrowed) return true;
      if (session.sourceAppointmentIdentityScheme === 'moments-person') {
        const seriesId = sessionSeriesId(session);
        if (seriesId && activeSeriesIds.has(seriesId)) return true;
        // Fails closed: an unattributable native row is withheld rather than
        // risk surfacing a hidden coach's session. The visible coach's own
        // native rows are attributable through either branch above.
        return visibleCoachPersonIds.has(session.coachId);
      }
      return visibleCoachIds.has(session.coachId);
    },
  };
};
